import React from 'react';

type Props = { children: React.ReactNode; fallback?: React.ReactNode };
type State = { hasError: boolean; message?: string };

const RELOAD_KEY = 'tc_chunk_reload';

/**
 * Returns true if the error is a failed dynamic import (stale chunk after deployment).
 * Bundlers throw errors like "Loading chunk X failed" or "ChunkLoadError" (Webpack)
 * and "Failed to fetch dynamically imported module" (Vite/ESM).
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; message?: string };
  if (e.name === 'ChunkLoadError') return true;
  if (typeof e.message === 'string') {
    return /loading chunk .* failed/i.test(e.message) || /failed to fetch dynamically imported module/i.test(e.message);
  }
  return false;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, message: error?.message || String(error) } as State;
  }

  componentDidCatch(error: any, info: any) {
    try {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info);
    } catch {}

    // Auto-reload once on stale chunk errors (happens after a new deployment)
    if (isChunkLoadError(error)) {
      try {
        const lastReload = sessionStorage.getItem(RELOAD_KEY);
        const now = Date.now();
        // Only auto-reload if we haven't reloaded in the last 30 seconds
        // (prevents infinite reload loops if the chunk is genuinely missing)
        if (!lastReload || now - parseInt(lastReload, 10) > 30_000) {
          sessionStorage.setItem(RELOAD_KEY, String(now));
          window.location.reload();
          return;
        }
      } catch {
        // sessionStorage may be unavailable in private browsing; reload unconditionally
        window.location.reload();
        return;
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div
            style={{
              color: 'var(--tc-text-primary, #FFFFFF)',
              background: 'var(--tc-background-primary, #000000)',
              minHeight: '100vh',
              padding: 24,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Something went wrong.</h3>
            <div style={{ color: 'var(--tc-text-secondary, #CCCCCC)', whiteSpace: 'pre-wrap', marginBottom: 16 }}>{this.state.message}</div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                fontSize: 13,
                background: 'var(--tc-status-info, #2196F3)',
                color: 'var(--tc-text-onAccent, #FFFFFF)',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Reload Page
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
